// Sincronizar en Mercado Libre los productos que la tienen tildada (ADR-2026-10-30-un-cambio-en-varias-tiendas).
//
//   · Los que ya están en Mercado Libre: a cada publicación suya se le escribe lo que cambió en la ficha y
//     ML deja cambiar desde acá (`cambioParaMl`: precio, descripción, stock, activa/pausada) y se le suman
//     las fotos nuevas que salieron de Atlas.
//   · Los que todavía no están: se crean allá con el alta de siempre (`publicarLanzamientoEnMl`).
//
// Devuelve un resumen para el aviso. Nunca tira: lo que falla se cuenta.

import { cambiarDatosEnMl, esperarCambioEnMl, publicacionesDelProducto } from "../../lib/mercadoLibreDatos";
import { subirFotosAMl } from "../../lib/mercadoLibreFotos";
import { publicarEnMl } from "../../lib/mercadoLibrePublicar";
import type { CambioDeCampo } from "./cambiosContraLaTienda";
import type { LanzamientoDetalle, ProductoDeLanzamiento } from "./lanzamientosApi";
import { publicarLanzamientoEnMl } from "./publicarLanzamiento";
import { cambioParaMl } from "./sincronizarEnVarias";

/** Las fotos que admite una publicación de Mercado Libre. */
const MAX_FOTOS_POR_ENVIO = 10;

export interface ResultadoEnMl {
  /** Publicaciones que ya existían y se cambiaron. */
  cambiadas: number;
  /** Productos que se mandaron a crear. */
  creados: number;
  /** Lo que salió mal, en una línea cada uno. */
  fallas: string[];
  /** Lo que cambió en la ficha y Mercado Libre no deja escribir desde acá. */
  noSeAplican: string[];
  /** Lo que no viajó a Mercado Libre y no es un error (sus límites), en una línea cada uno. */
  notas: string[];
  /** Las filas (ids de la ficha) que quedaron bien en todas sus publicaciones, o que se mandaron a crear. */
  listas: Set<string>;
}

export async function sincronizarEnMercadoLibre(p: {
  detalle: LanzamientoDetalle;
  /** `crearSiFalta`: Mercado Libre se tildó a propósito; si no está allá, se crea. Sin eso, sólo se edita. */
  productos: Array<{ producto: ProductoDeLanzamiento; cambios: readonly CambioDeCampo[]; crearSiFalta: boolean }>;
  brandId: string | null;
  cuentaConectada: boolean;
}): Promise<ResultadoEnMl> {
  const r: ResultadoEnMl = { cambiadas: 0, creados: 0, fallas: [], noSeAplican: [], notas: [], listas: new Set() };
  const aCrear: ProductoDeLanzamiento[] = [];
  const noSe = new Set<string>();

  for (const { producto, cambios, crearSiFalta } of p.productos) {
    if (!producto.productoId) continue;
    let publicaciones: Awaited<ReturnType<typeof publicacionesDelProducto>> = [];
    try {
      publicaciones = await publicacionesDelProducto(producto.productoId);
    } catch {
      r.fallas.push(`${producto.nombre}: no se pudo leer su publicación de Mercado Libre`);
      continue;
    }
    if (publicaciones.length === 0) {
      // No está allá y nadie pidió crearlo: no había nada que hacer, así que está bien (no es una falla).
      if (crearSiFalta) aCrear.push(producto);
      else r.listas.add(producto.id);
      continue;
    }
    let todoBien = true;
    const { cambio, noSeAplican } = cambioParaMl(producto, cambios);
    noSeAplican.forEach((x) => noSe.add(x));
    // Las fotos nuevas que salieron de Atlas (las subidas a mano no tienen cómo viajar a ML desde acá), hasta
    // las 10 que admite una publicación. Las que no van se dicen, no se pierden en silencio (review Greptile
    // de #232): en Tiendanube sí quedan, y en Mercado Libre no hay lugar o no hay cómo subirlas.
    const deAtlas = producto.imagenes.flatMap((i) =>
      i.imagenGeneradaId ? [{ imagenId: i.imagenGeneradaId, posicion: "secundaria" as const }] : [],
    );
    const fotos = deAtlas.slice(0, MAX_FOTOS_POR_ENVIO);
    const aMano = producto.imagenes.length - deAtlas.length;
    if (deAtlas.length > fotos.length) {
      r.notas.push(`${producto.nombre}: Mercado Libre admite hasta ${MAX_FOTOS_POR_ENVIO} fotos; ${deAtlas.length - fotos.length} no se subieron allá`);
    }
    if (aMano > 0) {
      r.notas.push(`${producto.nombre}: ${aMano === 1 ? "la foto subida a mano no se sube" : `las ${aMano} fotos subidas a mano no se suben`} a Mercado Libre desde acá`);
    }
    for (const pub of publicaciones) {
      let ok = true;
      if (cambio) {
        try {
          // Sincronizar es la confirmación: un precio muy distinto no vuelve a preguntar.
          const ejecucion = await cambiarDatosEnMl({ itemId: pub.item_id, ...cambio, confirmado: true });
          const res = await esperarCambioEnMl(ejecucion);
          const malos = Object.entries(res ?? {}).filter(([, v]) => v && !v.ok);
          // Sin resultado es que la espera venció: no se sabe si quedó, no es un éxito (review Greptile de #232).
          if (!res) {
            ok = false;
            r.fallas.push(`${producto.nombre}: Mercado Libre sigue procesando el cambio; revisalo en un rato`);
          } else if (malos.length > 0) {
            ok = false;
            const [campo, v] = malos[0]!;
            r.fallas.push(`${producto.nombre}: ${campo} — ${(v as { motivo?: string }).motivo ?? "no se pudo"}`);
          }
        } catch (e) {
          ok = false;
          r.fallas.push(`${producto.nombre}: ${e instanceof Error ? e.message : "no se pudo cambiar en Mercado Libre"}`);
        }
      }
      if (fotos.length > 0) {
        try {
          await subirFotosAMl(p.brandId, { itemId: pub.item_id, fotos, variationId: null, forzarPortada: false });
        } catch (e) {
          ok = false;
          r.fallas.push(`${producto.nombre}: las fotos no se pudieron subir (${e instanceof Error ? e.message : "error"})`);
        }
      }
      if (ok && (cambio || fotos.length > 0)) r.cambiadas++;
      if (!ok) todoBien = false;
    }
    if (todoBien) r.listas.add(producto.id);
  }

  if (aCrear.length > 0) {
    const creado = await publicarLanzamientoEnMl(
      { ...p.detalle, productos_: aCrear },
      {
        cuentaConectada: p.cuentaConectada,
        publicar: (productos, lanzamientoId) => publicarEnMl({ brandId: p.brandId, lanzamientoId, productos }),
      },
    );
    if (creado.motivo === "lanzado") {
      r.creados = creado.productos;
      for (const x of aCrear) r.listas.add(x.id);
    }
    else if (creado.motivo === "sin-fotos") r.fallas.push("Para crearlo en Mercado Libre le faltan fotos");
    else if (creado.motivo === "sin-cuenta") r.fallas.push("Para crearlo en Mercado Libre, conectá tu cuenta en Marca");
    else if (creado.motivo === "falta") r.fallas.push(`${creado.producto.nombre}: ${creado.texto}`);
    else if (creado.motivo === "rechazado") r.fallas.push(`Mercado Libre: ${creado.texto}`);
  }
  r.noSeAplican = [...noSe];
  return r;
}
